import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import { Input } from "common/blocks/Input";
import MarketingConsent from "common/blocks/MarketingConsent";
import UIIcon, { UIIconProps } from "common/blocks/UIIcon";
import { useComingSoonAlert } from "features/vehicleDetails/components/VehicleOverview/context";
import useLocalstorage from "hooks/useLocalstorage";
import TagManager from "react-gtm-module";
import { FormProvider, useForm } from "react-hook-form";
import { ResolvedPDPPage } from "resolvers/pages/pdpPageResolver";
import { VehicleService } from "services/vehicle/vehicle";
import { api } from "utils/fetch-api";
import { formatLabel } from "utils/formatLabel";
import generateGtmEcommerceEntryString from "utils/generateGtmEcommerceEntryString";
import { validatePostcode } from "utils/validatePostcode";
import { z } from "zod";

import styles from "./coming-soon.module.scss";

const cx = classNames.bind(styles);

type Alert = {
    id: string;
    name: string;
    createdAt: string;
    frequency: string;
    lastInteraction: string;
    email: string;
    status: string;
    customerId: string;
    deleteAt: string;
    stockBookNumber?: string;
    params: {
        key: string;
        values: string[] | number[];
    }[];
};

const ComingSoonAlertSchema = z.object({
    email: z
        .string()
        .min(1, {
            message: "Email is required",
        })
        .email({
            message: "Please enter a valid email address",
        }),
    contactNumber: z
        .string()
        .optional()
        .refine((v) => (!v ? true : /^(?:\+|0){1}(?:[0-9]{10,12})?$/.test(v)), {
            message: "Please enter a valid mobile number",
        }),
    postCode: z
        .string()
        .min(5, {
            message: "Please enter a minimum of 5 characters",
        })
        .max(10, {
            message: "Please enter a maximum of 10 characters",
        })
        .regex(/^[a-zA-Z]{1,2}\d[a-zA-Z\d]?\s*\d[a-zA-Z]{2}$/, {
            message: "Please enter a valid postcode",
        })
        .refine((v) => validatePostcode(v), { message: "Please enter a valid postcode" }),
    marketingConsent: z.boolean().default(false),
    contactSourceReason: z.string().default("PDP Coming Soon Alert"),
    contactSourceActivityType: z.string().default("Website"),
});

type ComingSoonAlertInputType = z.infer<typeof ComingSoonAlertSchema>;

type ResponseType =
    | "ALERTS_LIMIT_REACHED"
    | "ALERTS_LIMIT_PER_USER_REACHED"
    | "ALERT_ALREADY_EXISTS";

export function ComingSoonAlertModal({
    vehicle,
    content,
}: {
    vehicle: VehicleService.Vehicle;
    content: ResolvedPDPPage["widgetRowContent"]["alertSidePanel"];
}) {
    const [storedValue, setStoredValue] = useLocalstorage<string>("postcodeCapture", "");

    const methods = useForm<ComingSoonAlertInputType>({
        resolver: zodResolver(ComingSoonAlertSchema),
        mode: "onBlur",
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [completedMessage, setCompletedMessage] = useState<
        { title: string; message: string; icon?: UIIconProps["icon"] } | undefined
    >();

    const { alert } = useComingSoonAlert();

    // const watchPhone = methods.watch("phone");

    // useEffect(() => {
    //     if (watchPhone) {
    //         let input = "";

    //         watchPhone.split("").forEach((char, i) => {
    //             const charCode = char.charCodeAt(0);

    //             switch (charCode) {
    //                 case 43:
    //                     if (i === 0) {
    //                         input += char;
    //                     }
    //                     break;

    //                 default:
    //                     if (charCode >= 48 && charCode <= 57) {
    //                         input += char;
    //                     }
    //                     break;
    //             }
    //         });

    //         methods.setValue("phone", input);
    //     }
    // }, [watchPhone]);

    const responseMessages: Record<ResponseType, { title: string; message: string }> = {
        ALERTS_LIMIT_REACHED: {
            title: content.inactiveTitle,
            message: content.inactiveDescription,
        },
        ALERTS_LIMIT_PER_USER_REACHED: {
            title: content.maximumTitle,
            message: content.maximumDescription,
        },
        ALERT_ALREADY_EXISTS: {
            title: content.duplicateTitle,
            message: content.duplicateDescription,
        },
    };

    const submitComingSoonAlert = async (data: ComingSoonAlertInputType) => {
        if (!storedValue) setStoredValue(data.postCode);

        const { stockNo, ...alertRest } = alert!;

        const params: { key: string; values: string[] | number[] }[] = [];

        Object.entries(alertRest).forEach(([key, values]) =>
            params.push({
                key: key,
                values: [values],
            })
        );

        setLoading(true);

        const result = await api<Alert>("create-alert", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...data,
                name: `${alertRest.make} ${alertRest.model} - ${stockNo}`,
                frequency: "once",
                stockBookNumber: parseInt(stockNo),
                params,
            }),
        });

        setLoading(false);

        if (!result.ok) {
            if (result.status === 500) {
                setCompletedMessage({
                    icon: "Warning",
                    title: content.errorTitle,
                    message: content.errorDescription,
                });
            }

            if (result.status === 400) {
                setCompletedMessage({
                    icon: "Warning",
                    title: responseMessages[result.error as ResponseType].title,
                    message: responseMessages[result.error as ResponseType].message,
                });
            }
        } else {
            if (data.marketingConsent) {
                await axios.post("/api/customer", data);
            }

            const gtmEventData = generateGtmEcommerceEntryString(
                {
                    vehicle,
                    event: "coming_soon_alert",
                    itemListId: "pdp-coming-soon-alert",
                },
                true
            );

            TagManager.dataLayer({ dataLayer: gtmEventData as object });

            setCompletedMessage({
                icon: "AlertBell",
                title: content.successTitle,
                message: content.successDescription,
            });
        }
    };

    const handleCompletedReminder = () => {
        const event = new CustomEvent("close-sidebar");
        window.dispatchEvent(event);
    };

    if (completedMessage)
        return (
            <div className={cx("comingSoon", "completed")}>
                <UIIcon icon={completedMessage.icon ? completedMessage.icon : "Alert"} />
                <h2>{completedMessage.title}</h2>
                <p>{completedMessage.message}</p>
                <Button theme="primary" onClick={handleCompletedReminder} fullWidth>
                    Done
                </Button>
            </div>
        );

    return (
        <div className={cx("comingSoon")}>
            <p>{content.createAlertDescription}</p>
            <div className={cx("comingSoon-for")}>
                <UIIcon icon="Alert" />
                <div>
                    <p>{content.filtersTitle}</p>
                    <ul className={cx("comingSoon-for--items")}>
                        {Object.entries(alert!).map(([key, value], i) => (
                            <li key={key}>
                                {formatLabel(key)}: <span>{value}</span>{" "}
                                {i + 1 < Object.entries(alert!).length && "•"}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(submitComingSoonAlert)}>
                    <Input
                        id="email"
                        name="email"
                        label={content.emailAddressLabel}
                        helperText={content.emailAddressHelperText}
                        {...(!!content.emailAddressPlaceholderText && {
                            placeholder: content.emailAddressPlaceholderText,
                        })}
                    />
                    {/* <Input
                        id="contactNumber"
                        name="contactNumber"
                        label={content.mobileNumberLabel}
                        helperText={content.mobileNumberHelperText}
                        inputMode="numeric"
                        {...(!!content.mobileNumberPlaceholderText && {
                            placeholder: content.mobileNumberPlaceholderText,
                        })}
                    /> */}
                    <Input
                        id="postCode"
                        name="postCode"
                        label={content.postcodeLabel}
                        helperText={content.postcodeHelperText}
                        defaultValue={storedValue}
                        maxLength={10}
                        minLength={5}
                        {...(!!content.postcodePlaceholderText && {
                            placeholder: content.postcodePlaceholderText,
                        })}
                    />
                    {content?.marketingConsent && (
                        <MarketingConsent
                            primaryText={content.marketingConsent?.primaryText?.html}
                            secondaryText={content.marketingConsent?.secondaryText?.html}
                            image={content.marketingConsent?.image}
                            register={methods.register}
                        />
                    )}
                    <Button type="submit" theme="primary" fullWidth loading={loading}>
                        {content.ctaText}
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
}
